/**
 * /student/exam/:examId/setup — the pre-exam checks.
 *
 * Ordered steps; `Start` unlocks only when every one passes. The laptop is the
 * orchestrator: it publishes the current stage with `POST /api/attempts/:id/stage`
 * and the phone renders whatever it is told (docs/API.md §3.7).
 *
 * The paper-check and room-scan steps happen ON THE PHONE. The laptop does
 * not take the student's word for them — it gates on `media_counts`, i.e. on
 * what actually reached R2.
 *
 * The attempt row is created up front by `POST /api/attempts/prepare`, because
 * pairing needs something to pair *against*. That call does not start the
 * clock; `POST /api/attempts` at the end of this page does.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, Check, Mic, MicOff, Play } from "lucide-react";
import type {
  AttemptStage,
  AttemptStateResponse,
  IssuePairCodeResponse,
} from "@exam/shared";
import { AppShell } from "@/components/AppShell";
import { Button, Card, CardBody, CardHeader, useToast } from "@/components/ui";
import { isApiError } from "@/lib/api";
import {
  clearViewfinder,
  getAttempt,
  getDevices,
  heartbeat,
  issuePairCode,
  prepareAttempt,
  sendProctorEvents,
  setStage,
  startAttempt,
} from "@/lib/exam-api";
import { ServerClock, timed } from "@/lib/exam/serverClock";
import {
  meterWidth,
  SILENCE_GRACE_MS,
  useCamera,
  useMicLevel,
  useRecorder,
  useScreenShare,
} from "@/lib/recorder";
import { enterFullscreen } from "@/lib/proctor";
import { cn } from "@/lib/cn";
import { PairingPanel } from "./components/PairingPanel";
import { BITRATE_LADDER } from "@/lib/recorder";
import { PlacementDiagram } from "./components/PlacementDiagram";
import { PhoneViewfinder } from "./components/PhoneViewfinder";
import { ScreenShareStep } from "./components/ScreenShareStep";
import {
  SetupStepper,
  type StepDescriptor,
  type StepStatus,
} from "./components/SetupStepper";

/** Both sheets, front and back. */
const POLL_MS = 3_000;

type StepId = "webcam" | "screen" | "pairing" | "placement" | "rules";

/**
 * Every step there can be. Which ones a sitting actually uses depends on the
 * exam — see `stepsFor`.
 */
const ALL_STEPS: StepDescriptor[] = [
  {
    id: "webcam",
    title: "Camera and microphone",
    description: "Allow both, and check your face is in frame.",
  },
  {
    id: "screen",
    title: "Share your screen",
    description: "Share your entire screen — not a tab or window.",
  },
  {
    id: "pairing",
    title: "Connect your phone",
    description: "Scan the code to turn your phone into the side camera.",
  },
  {
    // Placed just before the rules so the phone is positioned in its final spot
    // right before the exam starts and never has to move again.
    id: "placement",
    title: "Position the phone",
    description: "Set the phone in its final side position — it stays put now.",
  },
  {
    id: "rules",
    title: "Exam rules",
    description: "Read and accept before starting.",
  },
];

/**
 * The steps this exam actually needs.
 *
 * The phone was mandatory, which made every sitting need a second device. That
 * suited an invigilated qualifier and is wrong here for a reason that is not
 * about proctoring: where these exams are sat the connection is poor, and a
 * phone recording is a THIRD video stream on the same household uplink. On a
 * slow link, insisting on it can cost you the screen recording — the one
 * recording nothing else can stand in for.
 *
 * Dropping the steps rather than showing them as optional is deliberate. An
 * optional step still has to be read, decided on and dismissed, and a student
 * about to sit an exam will do all three anxiously. If the exam does not want
 * a phone, the shortest honest setup is the one that never mentions one.
 */
function stepsFor(requirePhone: boolean): StepDescriptor[] {
  return requirePhone
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s.id !== "pairing" && s.id !== "placement");
}

/** Which stage the phone should be showing while a given step is open. */
const STAGE_FOR_STEP: Record<StepId, AttemptStage> = {
  webcam: "idle",
  screen: "idle",
  pairing: "idle",
  // The phone shows the "position me" screen through placement and stays there
  // while the student reads the rules.
  placement: "placement",
  rules: "placement",
};

export function SetupPage() {
  const { examId = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const clock = useMemo(() => new ServerClock(), []);

  const [state, setState] = useState<AttemptStateResponse | null>(null);
  const [pairing, setPairing] = useState<IssuePairCodeResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [starting, setStarting] = useState(false);

  const [activeStep, setActiveStep] = useState<StepId>("webcam");
  // Steps the student has explicitly confirmed on the laptop. Steps verified
  // server-side (pairing, paper, room scan) are NOT stored here — they are
  // derived from live state, so they cannot be faked by clicking.
  const [confirmed, setConfirmed] = useState<Set<StepId>>(new Set());

  // Audio ON. The room's sound used to arrive only with the phone, and the
  // phone is now optional and off by default — so on most sittings there is
  // now no microphone anywhere, and "somebody else is in the room talking to
  // them" became unprovable. It rides on the camera's own getUserMedia so the
  // browser asks once, for both, and it is best-effort: `useCamera` falls back
  // to video-only rather than let a refused microphone cost us the video.
  const camera = useCamera({ facingMode: "user", audio: true });
  const screen = useScreenShare();
  /** Guards the one-shot pair-code issue — see the effect below. */
  const pairIssuedRef = useRef(false);

  const attemptId = state?.attempt.id ?? null;

  // Defaults to FALSE while the attempt is still loading, so a phone-less exam
  // never flashes the pairing step into view before settling.
  const requirePhone = state?.exam.require_phone ?? false;
  const steps = useMemo(() => stepsFor(requirePhone), [requirePhone]);

  /**
   * Records a stall during setup.
   *
   * Sent straight through rather than via a queue: this fires at most a
   * handful of times, and the event is the whole point — a recording that
   * stopped and was never mentioned is the failure this app spent a night
   * fixing.
   */
  const reportSetupStall = useCallback(
    async (
      type: "WEBCAM_STOPPED" | "SCREEN_SHARE_STOPPED",
      info: {
        silentForMs: number;
        willRestart: boolean;
        reason: "encoder" | "storage";
      },
    ) => {
      const id = state?.attempt.id;
      if (!id) return;
      try {
        await sendProctorEvents(id, [
          {
            type,
            ts_ms: Math.round(clock.now()),
            severity: "critical",
            metadata: {
              during: "setup",
              reason:
                info.reason === "storage"
                  ? "could not save video locally"
                  : "recorder produced no video",
              silent_for_ms: info.silentForMs,
              recovering: info.willRestart,
            },
          },
        ]);
      } catch {
        // The recording is already in trouble; a failed report must not also
        // break the page trying to recover it.
      }
    },
    [state?.attempt.id, clock],
  );

  /* -- record from the moment permission is granted ------------------ */

  /**
   * Setup is recorded, not just the exam.
   *
   * The laptop used to start recording when the exam did, so everything
   * between granting camera access and pressing Start was unrecorded — which
   * is precisely the window in which the room is arranged, the phone is
   * placed, and anybody else in the room is still there. The phone has always
   * recorded from pairing (that is why its chunk offsets are negative); the
   * laptop simply did not.
   *
   * Two things make this safe now. Chunk offsets are computed from
   * `attempts.started_at` when the manifest is READ, so footage recorded
   * before the exam gets a correct negative offset the moment the exam starts —
   * nothing has to be rewritten. And chunks carry a recorder-session number
   * (migration 0012), so the navigation from this page to the runner ends one
   * MediaRecorder and begins another without the player trying to decode the
   * two as one stream.
   */
  const setupWebcam = useRecorder({
    attemptId,
    source: "webcam",
    stream: camera.stream,
    clock,
    videoBitsPerSecond: BITRATE_LADDER.webcam[0],
    // As soon as there is a camera and an attempt to file it under. It stops
    // when this page unmounts — either into the runner, which starts its own
    // recorder in a new session, or back to the exam list.
    recording: camera.state === "ready" && attemptId !== null,
    onStreamLost: () => void camera.request(),
    // Setup is now the window this recording exists to cover — the room being
    // arranged, the phone placed, anyone else still in the room. A stall here
    // that nobody records leaves an unexplained hole in exactly that stretch.
    onStall: ({ silentForMs, willRestart, reason }) =>
      void reportSetupStall("WEBCAM_STOPPED", {
        silentForMs,
        willRestart,
        reason,
      }),
  });

  const setupScreen = useRecorder({
    attemptId,
    source: "screen",
    stream: screen.stream,
    clock,
    videoBitsPerSecond: BITRATE_LADDER.screen[0],
    recording: screen.state === "ready" && attemptId !== null,
    onStall: ({ silentForMs, willRestart, reason }) =>
      void reportSetupStall("SCREEN_SHARE_STOPPED", {
        silentForMs,
        willRestart,
        reason,
      }),
  });

  const recordingNow =
    setupWebcam.status.recording || setupScreen.status.recording;

  /**
   * What is actually being captured, named one by one.
   *
   * Spelled out rather than shortened to "you are being recorded" because the
   * set genuinely varies now — the phone is optional, the microphone is
   * best-effort — and a student consenting to this is entitled to know which
   * of their devices is live, not a summary of it.
   */
  const recordingWhat = useMemo(() => {
    const parts = ["Your camera"];
    if (camera.hasAudio) parts.push("microphone");
    if (setupScreen.status.recording) parts.push("screen");
    const verb = parts.length > 1 ? "are" : "is";
    const list =
      parts.length > 1
        ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`
        : parts[0];
    return `${list} ${verb}`;
  }, [camera.hasAudio, setupScreen.status.recording]);

  /* -- prepare the attempt ------------------------------------------ */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const prepared = await timed(clock, () => prepareAttempt(examId));
        if (cancelled) return;
        setState(prepared);

        if (prepared.attempt.status === "in_progress") {
          // Already started (a refresh mid-exam) — do not make them redo setup.
          navigate(`/student/exam/${prepared.attempt.id}/run`, {
            replace: true,
          });
          return;
        }

        // Issuing a code ROTATES it and unbinds any phone already holding the
        // old one, so this must happen exactly once per visit — never on a
        // remount, and never while a phone is already connected. Only the
        // explicit "Get a new code" button may rotate.
        if (pairIssuedRef.current) return;
        pairIssuedRef.current = true;

        // An exam that does not use a phone gets no pair code at all. Minting
        // one anyway would leave a live device session nobody is going to
        // claim, and it is the code's existence that the phone page keys off.
        if (!prepared.exam.require_phone) return;

        // If a phone is already paired and beating (the student refreshed
        // mid-setup), reuse its code rather than rotating and knocking a
        // working camera offline.
        const livePhone = prepared.devices.find(
          (d) => d.role === "phone" && d.paired && d.alive,
        );

        if (livePhone) {
          const { devices } = await getDevices(prepared.attempt.id);
          const row = devices.find((d) => d.role === "phone");
          if (row && !cancelled) {
            setPairing({
              attempt_id: prepared.attempt.id,
              pair_code: row.pair_code,
              issued_at: row.connected_at ?? Date.now(),
              phone: livePhone,
            });
            return;
          }
        }

        const ticket = await issuePairCode(prepared.attempt.id);
        if (!cancelled) setPairing(ticket);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Could not prepare this exam.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [examId, clock, navigate]);

  /* -- poll state + laptop heartbeat -------------------------------- */

  useEffect(() => {
    if (!attemptId) return;

    const poll = async () => {
      try {
        // No paper needed here — this page never renders questions.
        const next = await timed(clock, () =>
          getAttempt(attemptId, { includePaper: false }),
        );
        setState(next);
        await timed(clock, () => heartbeat(attemptId, "laptop"));
      } catch {
        /* transient; the next tick retries */
      }
    };

    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [attemptId, clock]);

  /* -- publish the stage the phone should render -------------------- */

  useEffect(() => {
    if (!attemptId) return;
    const wanted = STAGE_FOR_STEP[activeStep];
    if (state?.stage === wanted) return;

    void setStage(attemptId, wanted).catch(() => {
      /* the next step change retries; not worth interrupting setup for */
    });
  }, [attemptId, activeStep, state?.stage]);

  /* -- derived step status ------------------------------------------ */

  const phone = state?.devices.find((d) => d.role === "phone");
  const phoneAlive = phone?.alive === true;

  const isDone = useCallback(
    (id: StepId): boolean => {
      switch (id) {
        case "webcam":
          return camera.state === "ready" && confirmed.has("webcam");
        case "screen":
          // Gated on a live monitor-level share, not just a click — a tab/window
          // share leaves the state anything but "ready".
          return screen.state === "ready" && confirmed.has("screen");
        case "pairing":
          return phoneAlive;
        case "placement":
          return phoneAlive && confirmed.has("placement");
        case "rules":
          return confirmed.has("rules");
      }
    },
    [camera.state, screen.state, confirmed, phoneAlive],
  );

  const statusOf = useCallback(
    (id: string): StepStatus => {
      const stepId = id as StepId;
      const index = steps.findIndex((s) => s.id === stepId);
      const previous = steps.slice(0, index).map((s) => s.id as StepId);

      if (isDone(stepId)) return "done";
      if (!previous.every(isDone)) return "locked";
      return "active";
    },
    [isDone, steps],
  );

  const allDone = steps.every((s) => isDone(s.id as StepId));

  /* -- actions -------------------------------------------------------- */

  const confirm = useCallback((id: StepId) => {
    setConfirmed((prev) => new Set(prev).add(id));
  }, []);

  // Record a not-whole-screen share for the admin, best-effort — the hard gate
  // is that Start stays locked until a monitor-level share is live.
  const logScreenNotFull = useCallback(() => {
    if (!attemptId) return;
    void sendProctorEvents(attemptId, [
      { ts_ms: Math.round(clock.now()), type: "SCREEN_SHARE_NOT_FULL" },
    ]).catch(() => undefined);
  }, [attemptId, clock]);

  const advance = useCallback(
    (from: StepId) => {
      const next = steps[steps.findIndex((s) => s.id === from) + 1];
      if (next) setActiveStep(next.id as StepId);
    },
    [steps],
  );

  const rotateCode = useCallback(async () => {
    if (!attemptId) return;
    setRotating(true);
    try {
      setPairing(await issuePairCode(attemptId));
    } catch {
      toast({ tone: "error", title: "Could not get a new pairing code" });
    } finally {
      setRotating(false);
    }
  }, [attemptId, toast]);

  const start = useCallback(async () => {
    if (!attemptId) return;
    setStarting(true);
    try {
      // Fullscreen must be requested inside this click — browsers refuse it
      // from anywhere else. Entering before the API call means the exam screen
      // opens already fullscreen.
      await enterFullscreen();
      const started = await timed(clock, () => startAttempt(examId));
      await setStage(attemptId, "recording").catch(() => undefined);
      navigate(`/student/exam/${started.attempt.id}/run`, { replace: true });
    } catch (err) {
      setStarting(false);
      toast({
        tone: "error",
        title: "Could not start the exam",
        description:
          isApiError(err) && err.message
            ? err.message
            : "Please try again in a moment.",
      });
    }
  }, [attemptId, examId, clock, navigate, toast]);

  /* -- render --------------------------------------------------------- */

  if (loadError) {
    return (
      <AppShell title="Exam setup">
        <Card>
          <CardBody>
            <h1 className="text-sm font-semibold text-ink">
              Could not prepare this exam
            </h1>
            <p className="mt-1 text-xs text-ink-muted">{loadError}</p>
            <Button
              className="mt-4"
              variant="secondary"
              onClick={() => navigate("/student")}
            >
              Back to my exams
            </Button>
          </CardBody>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell title="Exam setup">
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">
            {state?.exam.title ?? "Preparing…"}
          </h1>
          <p className="mt-0.5 text-xs text-ink-muted">
            Complete every check below. Your exam has not started and the timer
            is not running.
          </p>

          {/* Recording begins at permission, not at Start — so it has to be
              said here. A student who believes setup is private and finds out
              afterwards that it was filmed has been misled, however clearly
              the consent screen was worded. */}
          {recordingNow ? (
            <p className="mt-2 flex items-center gap-1.5 rounded-md border border-violation/30 bg-violation-soft px-3 py-2 text-xs text-violation">
              <span className="size-2 shrink-0 rounded-full bg-violation" />
              <span>
                <b>Recording has started.</b> {recordingWhat} being recorded
                from now until you submit — setup included.
              </span>
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row">
          <Card className="h-fit w-full shrink-0 overflow-hidden lg:w-72">
            <SetupStepper
              steps={steps}
              statusOf={statusOf}
              activeId={activeStep}
              onSelect={(id) => setActiveStep(id as StepId)}
            />
          </Card>

          <div className="min-w-0 flex-1">
            {activeStep === "webcam" ? (
              <WebcamStep
                camera={camera}
                done={isDone("webcam")}
                onConfirm={() => {
                  confirm("webcam");
                  advance("webcam");
                }}
              />
            ) : null}

            {activeStep === "screen" ? (
              <ScreenShareStep
                screen={screen}
                done={isDone("screen")}
                onConfirm={() => {
                  confirm("screen");
                  advance("screen");
                }}
                onNotFull={logScreenNotFull}
              />
            ) : null}

            {activeStep === "pairing" ? (
              <Card>
                <CardHeader
                  title="Connect your phone"
                  description="The phone is your side camera. Keep it plugged in and unlocked."
                />
                <CardBody>
                  <PairingPanel
                    pairCode={pairing?.pair_code ?? null}
                    joinUrl={
                      pairing
                        ? `${location.origin}/phone/${pairing.pair_code}`
                        : null
                    }
                    phone={phone}
                    phoneAlive={phoneAlive}
                    onRotate={() => void rotateCode()}
                    rotating={rotating}
                  />
                  <StepFooter
                    ready={phoneAlive}
                    readyLabel="Continue"
                    waitingLabel="Waiting for the phone…"
                    onNext={() => advance("pairing")}
                  />
                </CardBody>
              </Card>
            ) : null}

            {activeStep === "placement" ? (
              <PlacementStep
                attemptId={attemptId}
                phoneAlive={phoneAlive}
                onConfirm={() => {
                  confirm("placement");
                  advance("placement");
                  // The relayed frame is a picture of the student's room, and
                  // it has done its job the moment placement is agreed. Drop
                  // it rather than leaving it in the bucket for the rest of
                  // the exam. Failure is ignored: the phone stops publishing
                  // at the same moment anyway, so the worst case is one stale
                  // object, and refusing to advance over it would block the
                  // exam for nothing.
                  if (attemptId) void clearViewfinder(attemptId);
                }}
              />
            ) : null}

            {activeStep === "rules" ? (
              <RulesStep
                agreed={confirmed.has("rules")}
                requirePhone={requirePhone}
                onAgree={() => confirm("rules")}
              />
            ) : null}

            <Card className="mt-4">
              <CardBody className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-ink">
                    {allDone
                      ? "All checks passed."
                      : "Finish every check to start."}
                  </p>
                  <p className="mt-0.5 text-2xs text-ink-muted">
                    {allDone
                      ? "Starting will enter fullscreen and begin the timer."
                      : `${steps.filter((s) => isDone(s.id as StepId)).length} of ${steps.length} complete.`}
                  </p>
                </div>
                <Button
                  variant="primary"
                  icon={<Play />}
                  disabled={!allDone || starting}
                  onClick={() => void start()}
                >
                  {starting ? "Starting…" : "Start exam"}
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* ------------------------------------------------------------------ */
/* Steps                                                               */
/* ------------------------------------------------------------------ */

function StepFooter({
  ready,
  readyLabel,
  waitingLabel,
  onNext,
}: {
  ready: boolean;
  readyLabel: string;
  waitingLabel: string;
  onNext: () => void;
}) {
  return (
    <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
      {!ready ? (
        <span className="text-2xs text-ink-muted">{waitingLabel}</span>
      ) : null}
      <Button
        className="ml-auto"
        variant="primary"
        disabled={!ready}
        onClick={onNext}
      >
        {readyLabel}
      </Button>
    </div>
  );
}

function WebcamStep({
  camera,
  done,
  onConfirm,
}: {
  camera: ReturnType<typeof useCamera>;
  done: boolean;
  onConfirm: () => void;
}) {
  const ready = camera.state === "ready";

  return (
    <Card>
      <CardHeader
        title="Camera and microphone"
        description="These record you, and the room, for the whole exam."
      />
      <CardBody>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative shrink-0 overflow-hidden rounded-md border border-border bg-surface-sunken">
            <video
              ref={camera.videoRef}
              muted
              playsInline
              className="block h-[180px] w-[240px] object-cover"
            />
            {/* Framing guide — a face inside the oval is a face in frame. */}
            {ready ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                <div className="h-[130px] w-[100px] rounded-[50%] border border-dashed border-accent/50" />
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            {ready ? (
              <>
                <p className="flex items-center gap-1.5 text-xs text-ink">
                  <Check className="size-3.5 text-answered" />
                  Camera is on.
                </p>
                <ul className="mt-2 list-inside list-disc text-2xs text-ink-muted">
                  <li>Sit so your whole face is inside the outline.</li>
                  <li>Make sure the light is in front of you, not behind.</li>
                  <li>Do not cover the camera at any point.</li>
                </ul>
                <MicRow camera={camera} />
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="flex items-start gap-1.5 text-xs text-ink">
                  <Camera className="mt-0.5 size-3.5 shrink-0 text-ink-faint" />
                  {camera.errorMessage ??
                    "Allow camera access when your browser asks."}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void camera.request()}
                >
                  {camera.state === "requesting"
                    ? "Requesting…"
                    : "Allow camera"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
          {!ready ? (
            <span className="text-2xs text-ink-muted">
              The camera must be on to continue.
            </span>
          ) : null}
          <Button
            className="ml-auto"
            variant="primary"
            disabled={!ready}
            onClick={onConfirm}
          >
            {done ? "Continue" : "My face is in frame"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function PlacementStep({
  attemptId,
  phoneAlive,
  onConfirm,
}: {
  attemptId: string | null;
  phoneAlive: boolean;
  onConfirm: () => void;
}) {
  return (
    <Card>
      <CardHeader
        title="Position the phone"
        description="This is the last step before you start — set the phone in its final position and it must not move for the rest of the exam."
      />
      <CardBody>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="min-w-0">
            <div className="rounded-md border border-border bg-surface p-2">
              <PlacementDiagram className="h-[160px] w-full" />
            </div>

            <p className="mt-3 text-xs font-medium text-ink">
              The phone must see all four of these:
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-2xs text-ink-muted">
              <li>· The desk surface and your rough sheets</li>
              <li>· Both of your hands</li>
              <li>· The edge of your laptop screen</li>
              <li>· You, from the side</li>
            </ul>
            <p className="mt-3 text-2xs text-ink-faint">
              Stand it to one side and slightly behind you, propped against
              something solid.
            </p>
          </div>

          {/* The phone points away from the student, so its own screen is
              useless once it is placed. This is the only way to check the
              framing without picking it back up and losing the position. */}
          <div className="min-w-0">
            <p className="mb-1.5 text-xs font-medium text-ink">
              What the phone can see now
            </p>
            {attemptId && phoneAlive ? (
              <PhoneViewfinder attemptId={attemptId} />
            ) : (
              // Polling for a picture from a phone that is not connected would
              // sit on "waiting for the phone's picture" indefinitely while
              // generating a request a second — and point the student at the
              // wrong problem. The pairing step is the one to go back to.
              <p className="rounded-md border border-border bg-surface px-3 py-2 text-2xs text-ink-muted">
                The phone is not connected, so there is no picture to show. Go
                back a step and pair it again.
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end border-t border-border pt-3">
          <Button variant="primary" onClick={onConfirm}>
            The phone is in position
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The microphone's own line in the camera step.
 *
 * Separate from the camera's because it can fail on its own, and shown even
 * when it is working: "your room is being listened to" is not something to
 * leave a student to infer from a permission prompt they clicked past.
 *
 * It does NOT gate the step. A laptop with no working microphone is common,
 * the student cannot fix one while sitting an exam, and refusing to let them
 * start would trade the whole paper for the room audio. Instead the runner
 * logs `MIC_OFF` so the reviewer knows why the recording is silent — the
 * failure is made visible rather than made fatal.
 *
 * When the microphone IS on, the row shows what it is HEARING. "On" was the
 * only thing ever checked, and a device muted in Windows hands the browser a
 * perfectly live track that carries silence — so the app promised the room was
 * being recorded while a whole exam went down without a sound. A bar the
 * student can make move is the only honest version of that promise.
 */
function MicRow({ camera }: { camera: ReturnType<typeof useCamera> }) {
  const mic = useMicLevel(camera.stream, camera.hasAudio);

  if (camera.hasAudio) {
    // Nothing heard for a while. Not an error — a student who has simply not
    // spoken yet sees this too, and the fix for both is the same: say
    // something and watch the bar.
    const deaf =
      !mic.everHeard &&
      mic.silentForMs !== null &&
      mic.silentForMs > SILENCE_GRACE_MS;

    return (
      <div className="mt-3 border-t border-border pt-3">
        <p className="flex items-center gap-1.5 text-2xs text-ink-muted">
          <Mic
            className={cn(
              "size-3.5 shrink-0",
              deaf ? "text-warn" : "text-answered",
            )}
          />
          Microphone is on — the room is being recorded too.
          <span
            aria-hidden
            className="ml-1 h-1.5 w-24 overflow-hidden rounded-full bg-surface-sunken"
          >
            <span
              className={cn(
                "block h-full rounded-full transition-[width] duration-75",
                deaf ? "bg-warn" : "bg-answered",
              )}
              style={{ width: `${meterWidth(mic.level) * 100}%` }}
            />
          </span>
        </p>
        {deaf ? (
          <p className="mt-1.5 text-2xs text-warn">
            Say something — the bar is not moving. If it stays still, your
            microphone is muted or Windows is listening to a different one. You
            can still sit the exam; it will be recorded without sound.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-col items-start gap-2 border-t border-border pt-3">
      <p className="flex items-start gap-1.5 text-2xs text-ink-muted">
        <MicOff className="mt-0.5 size-3.5 shrink-0 text-warn" />
        <span>
          {camera.micErrorMessage ??
            "The microphone is not on. Your exam will be recorded without sound."}
        </span>
      </p>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => void camera.requestMic()}
      >
        {camera.micState === "requesting"
          ? "Requesting\u2026"
          : "Allow microphone"}
      </Button>
    </div>
  );
}

/**
 * The rules, for a sitting that does or does not use a phone.
 *
 * A function rather than a constant because one of them names the devices, and
 * naming a phone this exam never asked for is the same failure the server had:
 * telling the student about a step that does not exist here.
 */
function rulesFor(requirePhone: boolean): string[] {
  return [
    "The exam runs in fullscreen. Leaving fullscreen is recorded.",
    "Do not switch tabs or windows. Switching away is recorded.",
    requirePhone
      ? "Both cameras — laptop and phone — must stay on for the whole exam."
      : "Your camera and microphone must stay on for the whole exam.",
    "Copying and pasting is disabled, and any attempt is recorded.",
    "Only your rough sheets, a pen and a calculator are allowed on the desk.",
    "Nobody else may be in the room or speak to you during the exam — the microphone is recording.",
    "The timer is set by the server and cannot be paused.",
  ];
}

function RulesStep({
  agreed,
  requirePhone,
  onAgree,
}: {
  agreed: boolean;
  requirePhone: boolean;
  onAgree: () => void;
}) {
  const [checked, setChecked] = useState(agreed);
  const rules = rulesFor(requirePhone);

  return (
    <Card>
      <CardHeader
        title="Exam rules"
        description="Read these before starting. Breaking them is recorded for review — it does not end your exam automatically."
      />
      <CardBody>
        <ul className="flex flex-col divide-y divide-border border-y border-border">
          {rules.map((rule) => (
            <li key={rule} className="py-2 text-xs text-ink-muted">
              {rule}
            </li>
          ))}
        </ul>

        <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-md border border-border-strong bg-surface px-3 py-2.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              setChecked(e.target.checked);
              if (e.target.checked) onAgree();
            }}
            className="mt-0.5 size-3.5 shrink-0 accent-accent"
          />
          <span className="text-xs text-ink">
            I have read the rules and I agree to follow them. I understand that
            fullscreen is required, that tab switching is recorded, and that my
            camera and microphone must stay on.
          </span>
        </label>
      </CardBody>
    </Card>
  );
}
